'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import ReactDOM from 'react-dom'

import { Box, chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { KebabVerticalIcon } from '../Icon'
import { Tooltip } from '../Tooltip'

export type TableMenuButtonItem = {
  label: string
  icon?: React.ReactNode
  onClick: () => void
  color?: string
  disabled?: boolean
  disabledTooltipLabel?: string
}

export type TableMenuButtonProps = {
  items: TableMenuButtonItem[]
  colorScheme: ColorScheme
}

export const TableMenuButton = ({
  items,
  colorScheme = 'light',
}: TableMenuButtonProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [disabledTooltip, setDisabledTooltip] = useState<{
    anchorEl: HTMLElement
    label: string
  } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const isDark = colorScheme !== 'light'
  const prefix = isDark ? '' : 'light.'

  const handleTriggerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right + window.scrollX,
      })
    }
    setIsOpen((prev) => !prev)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  useEffect(() => {
    if (isOpen) return
    setDisabledTooltip(null)
  }, [isOpen])

  // A menu with no items would open to an empty panel, so the trigger itself is
  // not rendered.
  if (items.length === 0) return null

  return (
    <>
      <chakra.button
        _hover={{ bg: `${prefix}interactive.icon-button-hover-bg` }}
        alignItems='center'
        aria-expanded={isOpen}
        aria-haspopup='menu'
        aria-label='Options'
        bg='transparent'
        border='none'
        borderRadius='4px'
        cursor='pointer'
        display='flex'
        h='36px'
        justifyContent='center'
        onClick={handleTriggerClick}
        ref={triggerRef}
        type='button'
        w='36px'
      >
        <KebabVerticalIcon colorScheme={colorScheme} size={20} />
      </chakra.button>
      {isOpen &&
        ReactDOM.createPortal(
          <Box
            bg={`${prefix}bg.card`}
            border='1px solid'
            borderColor={`${prefix}border.subtle`}
            borderRadius='4px'
            boxShadow='md'
            left={`${position.left}px`}
            minWidth='140px'
            position='absolute'
            ref={(node: HTMLDivElement | null) => {
              menuRef.current = node
            }}
            role='menu'
            top={`${position.top}px`}
            transform='translateX(-100%)'
            zIndex={1500}
          >
            {items.map((item) => (
              <Box
                _hover={
                  item.disabled
                    ? undefined
                    : { bg: `${prefix}bg.table-row-hover` }
                }
                alignItems='center'
                aria-disabled={item.disabled}
                color={item.color ?? `${prefix}text.primary`}
                cursor={item.disabled ? 'not-allowed' : 'pointer'}
                display='flex'
                fontFamily="'Soehne', 'Söhne', sans-serif"
                fontSize='12px'
                fontWeight='400'
                gap='8px'
                key={item.label}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  if (item.disabled) return
                  setDisabledTooltip(null)
                  item.onClick()
                  setIsOpen(false)
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                  if (!item.disabled || !item.disabledTooltipLabel) return
                  setDisabledTooltip({
                    anchorEl: e.currentTarget,
                    label: item.disabledTooltipLabel,
                  })
                }}
                onMouseLeave={() => setDisabledTooltip(null)}
                opacity={item.disabled ? 0.45 : 1}
                px='12px'
                py='8px'
                role='menuitem'
              >
                {item.icon && (
                  <Box as='span' flexShrink={0}>
                    {item.icon}
                  </Box>
                )}
                {item.label}
              </Box>
            ))}
            <Tooltip
              anchorEl={disabledTooltip?.anchorEl ?? null}
              colorScheme={colorScheme}
              label={disabledTooltip?.label ?? ''}
              placement='left'
            />
          </Box>,
          document.body
        )}
    </>
  )
}
